import { useCallback, useMemo, useState } from 'react';
import { css } from '@emotion/react';
import { VoicemailGreetingType_Enum } from '@weave/schema-gen-ts/dist/schemas/phone/callroute/v1/callroute_service.pb';
import { VoicemailBox } from '@weave/schema-gen-ts/dist/schemas/phone/voicemail-config/v2/voicemail_config.pb';
import { CallRouteV1 } from '@frontend/api-call-route';
import { useTranslation } from '@frontend/i18n';
import { sentry } from '@frontend/tracking';
import { theme } from '@frontend/theme';
import {
  Button,
  DropdownField,
  SkeletonLoaders,
  useAlert,
  useFormField,
  Text,
  useMultiStepModalControl,
} from '@frontend/design-system';
import {
  AudioPicker,
  AudioPickerProps,
  DEFAULT_GREETING_ID,
  DEFAULT_GREETING_PATH,
} from '../../../audio-picker/audio-picker';
import { AudioItem } from '../../../audio-picker/types';
import { CreateModal } from '../../../voicemail-box/new/create-button';
import { VoicemailBoxCreateResponse } from '../../../voicemail-box/new/types';
import { hasVoicemailExpansion } from '../call-route-flow/data';
import { Step } from './atoms/step';
import { AddStepPanelProps } from './types';

export const voicemailAllowedOptions: AudioPickerProps['allowedOptions'] = {
  add: true,
  custom: true,
  standard: false,
  mailbox: true,
  none: true,
  default: true,
};

type GroupedVoicemailBoxes = {
  linkedVoicemailBox?: VoicemailBox;
  voicemailBoxes: VoicemailBox[];
};

export const VoicemailStep = ({
  tenantId,
  settingsTenantLocation,
  initialState,
  callObjectsData,
  isDeviceExtensionRoute = false,
  callRoute,
  onProceedClick,
}: AddStepPanelProps<CallRouteV1.Types.NodeType.NODE_TYPE_VOICEMAIL_BOX>) => {
  const { t } = useTranslation('phone');
  const alerts = useAlert();

  const { triggerProps, ...createVoicemailModalProps } = useMultiStepModalControl(
    { onComplete: () => {}, onCancel: () => {}, showCancelAction: true },
    { onClose: () => {} }
  );

  const handleOpenCreateVoicemailBox = () => {
    createVoicemailModalProps.goToStep({ orIndex: 0 });
    triggerProps.onClick();
  };

  const [selectedAudioFile, setSelectedAudioFile] = useState<AudioItem | undefined>(undefined);

  const { voicemailBoxes = [], voicemailBoxesIsLoading } = callObjectsData;

  const linkedVoicemailBoxId = callRoute?.deviceExtension?.voicemailBoxId;

  const groupedVoicemailBoxes = useMemo(() => {
    const grouped = voicemailBoxes.reduce<GroupedVoicemailBoxes>(
      (acc, box) => {
        if (isDeviceExtensionRoute && box.mailbox.id === linkedVoicemailBoxId) {
          acc.linkedVoicemailBox = box;
        } else {
          acc.voicemailBoxes.push(box);
        }

        return acc;
      },
      { voicemailBoxes: [] }
    );
    return grouped;
  }, [voicemailBoxes]);

  const getInitialGreetingMediaValue = () => {
    const callObject = initialState?.callObject;

    if (!hasVoicemailExpansion(callObject)) {
      return 'default';
    }

    const greetingId = callObject.voicemailExpansion.greetingMediaItemId;
    const greetingType = callObject.voicemailExpansion.greetingType;

    if (greetingType === VoicemailGreetingType_Enum.NO_GREETING) {
      return 'none';
    }

    if (greetingType === VoicemailGreetingType_Enum.DEFAULT_GREETING) {
      return 'default';
    }

    if (!greetingId) {
      return 'default';
    }

    return greetingId;
  };

  const voicemailBoxProps = useFormField({
    required: true,
    type: 'dropdown',
    value: initialState?.callObject?.primitiveId,
  });

  const greetingMediaProps = useFormField({
    required: true,
    type: 'dropdown',
    value: getInitialGreetingMediaValue(),
  });

  const handleProceedClick = useCallback(() => {
    const voicemailBox = voicemailBoxes?.find((vm) => vm.mailbox.id === voicemailBoxProps.value);

    if (!voicemailBox) {
      alerts.error('Unknown voicemail box data');
      sentry.error({
        error: 'Voicemail box not found in Voicemail step',
        topic: 'phone-config',
        team: 'fe-phone-config',
        severityLevel: 'error',
        addContext: {
          name: 'Step data',
          context: {
            voicemailBoxes,
            selectedVoicemailBoxId: voicemailBoxProps.value,
          },
        },
      });
      return;
    }

    let greetingMediaFileName;
    let greetingMediaItemId;
    let downloadUrl;
    let greetingType;
    if (greetingMediaProps.value === 'none') {
      greetingMediaFileName = '';
      greetingMediaItemId = '';
      downloadUrl = '';
      greetingType = VoicemailGreetingType_Enum.NO_GREETING;
    } else if (greetingMediaProps.value === 'default') {
      greetingMediaFileName = 'Default Greeting';
      greetingMediaItemId = DEFAULT_GREETING_ID;
      downloadUrl = DEFAULT_GREETING_PATH;
      greetingType = VoicemailGreetingType_Enum.DEFAULT_GREETING;
    } else if (selectedAudioFile) {
      greetingMediaFileName = selectedAudioFile.name;
      greetingMediaItemId = selectedAudioFile.id;
      downloadUrl = selectedAudioFile.path;
      if (selectedAudioFile.type === 'mailbox') {
        greetingType = VoicemailGreetingType_Enum.NUMBERED_GREETING;
      } else {
        greetingType = VoicemailGreetingType_Enum.AUDIO_LIBRARY_GREETING;
      }
    } else {
      alerts.error('Unknown greeting data');
      return;
    }

    onProceedClick({
      type: CallRouteV1.Types.NodeType.NODE_TYPE_VOICEMAIL_BOX,
      callObject: {
        primitiveId: voicemailBox.mailbox.id,
        primitiveName: voicemailBox.mailbox.name,
        instructionId: initialState?.callObject?.instructionId ?? '',
        instructionSetId: initialState?.callObject?.instructionSetId ?? '',
        voicemailExpansion: {
          greetingMediaFileName,
          greetingMediaItemId,
          downloadUrl,
          greetingType,
        },
      },
    });
  }, [
    alerts,
    voicemailBoxes,
    voicemailBoxProps.value,
    greetingMediaProps.value,
    selectedAudioFile,
    onProceedClick,
    initialState?.callObject?.instructionId,
    initialState?.callObject?.instructionSetId,
  ]);

  const handleOnCreateSuccess = (data: VoicemailBoxCreateResponse) => {
    const mailBoxId = data.mailbox.id;

    voicemailBoxProps.onChange({
      // @ts-expect-error This is complaining thinking that value does not exist on the event target,
      // but it does.
      target: { value: mailBoxId },
    });
  };

  return (
    <>
      <Step
        title={t('Voicemail')}
        primaryButtonDisabled={!voicemailBoxProps.value || !greetingMediaProps.value}
        onPrimaryButtonClick={handleProceedClick}
      >
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: ${theme.spacing(3)};
          `}
        >
          <SkeletonLoaders.Loader isLoading={voicemailBoxesIsLoading} shape='rectangle' width={320} height={50}>
            <DropdownField
              {...voicemailBoxProps}
              css={css`
                max-width: 300px;
              `}
              name='voicemailBoxId'
              label={t('Select Voicemail Box')}
            >
              <DropdownField.Option
                css={css`
                  padding: 0;
                `}
                key={'create-voicemail-box'}
                value=''
                isSelectable={false}
              >
                <Button
                  iconName='plus'
                  variant='tertiary'
                  onClick={handleOpenCreateVoicemailBox}
                  css={css`
                    width: 100%;
                    justify-content: start;
                  `}
                >
                  {t('Create Voicemail Box')}
                </Button>
              </DropdownField.Option>

              {!!groupedVoicemailBoxes.linkedVoicemailBox && (
                <DropdownField.Option
                  key={groupedVoicemailBoxes.linkedVoicemailBox.mailbox.id}
                  value={groupedVoicemailBoxes.linkedVoicemailBox.mailbox.id}
                  displayValue={groupedVoicemailBoxes.linkedVoicemailBox.mailbox.name}
                >
                  <div
                    css={css`
                      display: flex;
                      flex-direction: column;
                      gap: ${theme.spacing(0.5)};
                    `}
                  >
                    <Text as={'span'}>{groupedVoicemailBoxes.linkedVoicemailBox.mailbox.name}</Text>
                    <Text as={'span'} size='small' color='subdued'>
                      {t('Linked Voicemail Box')}
                    </Text>
                  </div>
                </DropdownField.Option>
              )}

              {groupedVoicemailBoxes.voicemailBoxes?.map((option) => (
                <DropdownField.Option key={option.mailbox.id} value={option.mailbox.id}>
                  {option.mailbox.name}
                </DropdownField.Option>
              ))}
            </DropdownField>
          </SkeletonLoaders.Loader>

          {voicemailBoxProps.value && (
            <AudioPicker
              widths={{ field: '300px', scrubber: '240px' }}
              allowedOptions={voicemailAllowedOptions}
              mailboxId={voicemailBoxProps.value}
              field={greetingMediaProps}
              name='greetingMediaId'
              labels={{
                field: t('Select Voicemail Greeting'),
              }}
              onSelect={(item) => {
                // NOTE: this callback function will not be called if the user selects the "No Greeting option". The
                // value will still be set on the field (greetingMediaProps.value), but the onSelect function will not
                // be called.
                setSelectedAudioFile(item);
              }}
              tenantId={tenantId}
            />
          )}
        </div>
      </Step>

      {settingsTenantLocation && (
        <CreateModal
          {...createVoicemailModalProps}
          tenantLocation={settingsTenantLocation}
          onSuccess={handleOnCreateSuccess}
        />
      )}
    </>
  );
};
